import { DragDropProvider } from '@dnd-kit/react'
import type { DragEndEvent } from '@dnd-kit/react'
import { CONFIG_ELEMENTS, GroupingGroup, GroupingItem } from '@vedalib/editor/lib/elements'
import { produce } from 'immer'
import lodash from 'lodash'
import { useEffect, useRef } from 'react'
import { useContextSelector } from 'use-context-selector'

import { useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import { useProjectT } from 'services/ProjectLabels'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlockId } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { createDndAccessibilityPlugin, focusFirstFocusableInElement } from 'utils/accessibility'

import GroupingContext from '../../GroupingContext'
import Result from '../Result'
import * as s from './DefaultElement.module.scss'
import DestinationCard from './DestinationCard'
import PortalGroupingItem from './PortalGroupingItem'

const isGroup = (entity: GroupingItem | GroupingGroup): entity is GroupingGroup => {
  return (entity as GroupingGroup).target !== undefined
}

const getName = (entity: GroupingItem | GroupingGroup | null | undefined) => {
  if (!entity) {
    return undefined
  }

  if (isGroup(entity)) {
    return entity.target.label || entity.target.image?.accessibility || entity.value
  }

  return entity?.label || entity?.image?.accessibility || entity?.value
}

const DefaultElement = () => {
  const styles = useContextSelector(GroupingContext, (state) => state.styles)
  const shuffledAnswers = useContextSelector(GroupingContext, (state) => state.shuffledAnswers)
  const validationMap = useContextSelector(GroupingContext, (state) => state.validationMap)
  const destination = useContextSelector(GroupingContext, (state) => state.destination)
  const testResult = useContextSelector(GroupingContext, (state) => state.testResult)
  const setDestination = useContextSelector(GroupingContext, (state) => state.setDestination)
  const setTestResult = useContextSelector(GroupingContext, (state) => state.setTestResult)
  const elementId = useContextSelector(GroupingContext, (state) => state.elementId) || ''
  const setShuffledAnswers = useContextSelector(
    GroupingContext,
    (state) => state.setShuffledAnswers,
  )
  const showResult = shuffledAnswers.length === 0

  const pt = useProjectT()

  const announce = useLiveFeedback('polite')

  const blockUuid = useProjectContext(getBlockId) || ''
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: blockUuid,
      elementType: CONFIG_ELEMENTS.grouping.type,
      elementId,
    },
  )

  const containerRef = useRef<HTMLDivElement>(null)

  const onDragEnd = ({ operation, canceled }: Parameters<DragEndEvent>[0]) => {
    if (canceled || !operation.source || !operation.target) {
      return
    }
    const itemId = String(operation.source.id)
    const groupId = String(operation.target.id)
    const el = shuffledAnswers.find((a) => a.value === itemId)
    if (!el) {
      return
    }

    setTestResult({
      ...testResult,
      [el.value]: validationMap[el.value] === groupId,
    })
    const [firstCard, secondCard] = Object.values(validationMap)
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        itemLabel: el.label,
        result: validationMap[el.value] === groupId,
        validationCardFirstId: firstCard,
        validationCardSecondId: secondCard,
      },
      objectExtensions: { itemId: el.value, ...baseObjectExtensions },
      elementId,
    })
    sendXapiData(data)

    const newDest = produce(destination, (draft) => {
      const dest = draft.find((e) => e.value === groupId)
      dest?.items.push(el)
    })

    setDestination(newDest)
    const remaining = shuffledAnswers.filter((e) => e.value !== itemId)
    setShuffledAnswers(remaining)

    if (remaining.length > 0) {
      setTimeout(() => {
        if (containerRef.current) {
          focusFirstFocusableInElement(containerRef.current)
        }
      }, 100)
    }
  }

  useEffect(() => {
    if (!showResult || !Object.keys(testResult).length) {
      return
    }
    const total = Object.values(testResult).length
    const correct = Object.values(testResult).filter(Boolean).length
    announce(`${correct} / ${total} ${pt('elements.grouping.result')}`)
    if (containerRef.current) {
      focusFirstFocusableInElement(containerRef.current)
    }
  }, [announce, pt, showResult, testResult])

  return (
    <DragDropProvider
      onDragEnd={onDragEnd}
      plugins={(defaults) => [...defaults, createDndAccessibilityPlugin(getName)]}
    >
      <div ref={containerRef}>
        {shuffledAnswers.length === 0 && <Result />}
        {shuffledAnswers.length > 0 && (
          <div
            aria-label='Answer cards'
            role='region'
            style={{
              ...lodash.omit(styles.answer, 'borderStyle'),
              backgroundColor: styles.answer.backgroundColor || undefined,
              position: 'relative',
              width: 200,
              height: 200,
              margin: '0 auto',
            }}
          >
            {shuffledAnswers.map((item, index) => (
              <PortalGroupingItem
                isActive={index === shuffledAnswers.length - 1}
                item={item}
                key={item.value}
              />
            ))}
          </div>
        )}
      </div>
      <div
        aria-label='Drop zones'
        className={s.destContainer}
        role='list'
        style={{ gap: styles.indents.gap }}
      >
        {destination.map((e) => (
          <DestinationCard group={e} key={e.value} />
        ))}
      </div>
    </DragDropProvider>
  )
}

export default DefaultElement
